import { ProblemData } from "../../MathFactsGenerator/Constants.ts";
import { cardFaces } from "../cardFaces.ts";
import { expectedAnswer } from "../Quiz/answer.ts";
import { QuizQueue } from "../Quiz/quizQueue.ts";

/*
 * The falling-facts game, as pure functions over a state value (ADR 0053 §8).
 *
 * No timers, no DOM, no React: the component calls `advance` once per
 * animation frame with the frame's delta, and `submit` when Enter is pressed.
 * Everything that makes the game a game - spawning, falling, landing, clearing,
 * levelling - is here and is tested by calling these with numbers.
 */

export const LIVES = 3;
export const COUNTDOWN_MS = 3000;
export const LANES = 4;
export const MAX_ON_SCREEN = 6;
export const CLEARS_PER_LEVEL = 10;
/** A stalled frame loses the child this much at most, never the whole field. */
export const MAX_FRAME_MS = 250;
/** How long a cleared fact's burst lingers where it was hit. */
export const BURST_MS = 400;
/** Draws to find a fact whose answer is not already falling (§2). */
const DISTINCT_ANSWER_TRIES = 5;

/**
 * The drop game's best-score key (ADR 0098 §1): the operations played, sorted
 * and joined, then `:drop`. One operation gives `Multiplication:drop`, exactly
 * what the multiplication-only game saved before the picker, so those bests
 * survive. Never compared with a timed quiz score.
 */
export function dropBestScoreKey(operations: string[]): string {
    return `${[...operations].sort().join("+")}:drop`;
}

export function fallMsFor(level: number): number {
    return Math.max(5000, 14000 * Math.pow(0.9, level - 1));
}

export function spawnMsFor(level: number): number {
    return Math.max(1400, 4000 * Math.pow(0.9, level - 1));
}

export function levelFor(cleared: number): number {
    return 1 + Math.floor(cleared / CLEARS_PER_LEVEL);
}

export interface Drop {
    id: number;
    /** As shown, e.g. "7 x 8". */
    front: string;
    answer: number;
    lane: number;
    /** Play time since this fact appeared. */
    ageMs: number;
    /** The fall time of the level it spawned at; it keeps that speed. */
    fallMs: number;
}

export interface Burst {
    id: number;
    answer: number;
    lane: number;
    /** How far down the fact was when it was cleared, 0 to 1. */
    progress: number;
    ageMs: number;
}

export interface LandedFact {
    front: string;
    answer: number;
}

export type GameEvent =
    | { kind: "cleared"; seq: number }
    | { kind: "wrong"; seq: number }
    | { kind: "landed"; seq: number };

export interface DropGameState {
    phase: "countdown" | "playing" | "over";
    countdownLeftMs: number;
    sinceSpawnMs: number;
    nextId: number;
    drops: Drop[];
    bursts: Burst[];
    lives: number;
    cleared: number;
    wrongGuesses: number;
    landed: LandedFact[];
    /** Every fact cleared, as shown - for the missed-facts store (ADR 0012). */
    clearedFronts: string[];
    /** The latest thing that happened, for feedback; `seq` retriggers it. */
    lastEvent: GameEvent | null;
}

export function startGame(): DropGameState {
    return {
        phase: "countdown",
        countdownLeftMs: COUNTDOWN_MS,
        sinceSpawnMs: 0,
        nextId: 1,
        drops: [],
        bursts: [],
        lives: LIVES,
        cleared: 0,
        wrongGuesses: 0,
        landed: [],
        clearedFronts: [],
        lastEvent: null,
    };
}

export function progressOf(drop: Drop): number {
    return Math.min(1, drop.ageMs / drop.fallMs);
}

function nextSeq(state: DropGameState): number {
    return (state.lastEvent?.seq ?? 0) + 1;
}

/**
 * The lane whose most recent fact has fallen furthest, or an empty one - so a
 * new fact never appears on top of the last.
 */
function pickLane(drops: Drop[]): number {
    let best = 0;
    let bestProgress = -1;
    for (let lane = 0; lane < LANES; lane++) {
        const inLane = drops.filter((d) => d.lane === lane);
        if (inLane.length === 0) {
            return lane;
        }
        const newest = Math.min(...inLane.map(progressOf));
        if (newest > bestProgress) {
            bestProgress = newest;
            best = lane;
        }
    }
    return best;
}

function drawDistinct(queue: QuizQueue, drops: Drop[]): ProblemData {
    const onScreen = new Set(drops.map((d) => d.answer));
    let problem = queue.next();
    for (let i = 1; i < DISTINCT_ANSWER_TRIES && onScreen.has(expectedAnswer(problem)); i++) {
        problem = queue.next();
    }
    return problem;
}

function spawn(state: DropGameState, queue: QuizQueue): DropGameState {
    const problem = drawDistinct(queue, state.drops);
    const drop: Drop = {
        id: state.nextId,
        front: cardFaces(problem).front,
        answer: expectedAnswer(problem),
        lane: pickLane(state.drops),
        ageMs: 0,
        fallMs: fallMsFor(levelFor(state.cleared)),
    };
    return {
        ...state,
        nextId: state.nextId + 1,
        sinceSpawnMs: 0,
        drops: [...state.drops, drop],
    };
}

/** Moves the game on by one frame's worth of play time. */
export function advance(state: DropGameState, frameMs: number, queue: QuizQueue): DropGameState {
    if (state.phase === "over") {
        return state;
    }
    const dt = Math.max(0, Math.min(frameMs, MAX_FRAME_MS));

    if (state.phase === "countdown") {
        const left = state.countdownLeftMs - dt;
        if (left > 0) {
            return { ...state, countdownLeftMs: left };
        }
        return spawn({ ...state, phase: "playing", countdownLeftMs: 0 }, queue);
    }

    let next: DropGameState = {
        ...state,
        sinceSpawnMs: state.sinceSpawnMs + dt,
        drops: state.drops.map((d) => ({ ...d, ageMs: d.ageMs + dt })),
        bursts: state.bursts
            .map((b) => ({ ...b, ageMs: b.ageMs + dt }))
            .filter((b) => b.ageMs < BURST_MS),
    };

    const landing = next.drops.filter((d) => d.ageMs >= d.fallMs);
    if (landing.length > 0) {
        const lives = Math.max(0, next.lives - landing.length);
        next = {
            ...next,
            drops: next.drops.filter((d) => d.ageMs < d.fallMs),
            lives,
            landed: [...next.landed, ...landing.map((d) => ({ front: d.front, answer: d.answer }))],
            lastEvent: { kind: "landed", seq: nextSeq(next) },
        };
        if (lives === 0) {
            return { ...next, phase: "over", drops: [], bursts: [] };
        }
    }

    const due = next.sinceSpawnMs >= spawnMsFor(levelFor(next.cleared));
    if ((due || next.drops.length === 0) && next.drops.length < MAX_ON_SCREEN) {
        next = spawn(next, queue);
    }
    return next;
}

/**
 * Enter was pressed with `typed` in the box. Clears the matching fact nearest
 * the ground, or counts a wrong guess. An empty box does nothing - there is no
 * skip, because the fact keeps falling either way (§3).
 */
export function submit(state: DropGameState, typed: string): DropGameState {
    const trimmed = typed.trim();
    if (state.phase !== "playing" || trimmed === "") {
        return state;
    }
    const value = /^\d+$/.test(trimmed) ? Number(trimmed) : NaN;
    const matches = state.drops.filter((d) => d.answer === value);
    if (matches.length === 0) {
        return {
            ...state,
            wrongGuesses: state.wrongGuesses + 1,
            lastEvent: { kind: "wrong", seq: nextSeq(state) },
        };
    }
    const hit = matches.reduce((a, b) => (progressOf(b) > progressOf(a) ? b : a));
    return {
        ...state,
        drops: state.drops.filter((d) => d.id !== hit.id),
        bursts: [
            ...state.bursts,
            { id: hit.id, answer: hit.answer, lane: hit.lane, progress: progressOf(hit), ageMs: 0 },
        ],
        cleared: state.cleared + 1,
        clearedFronts: [...state.clearedFronts, hit.front],
        lastEvent: { kind: "cleared", seq: nextSeq(state) },
    };
}
